import { useState, useEffect, useCallback } from "react";
import api from "@/services/api";
import {
  Shield, ShieldCheck, ShieldAlert, ShieldX, ShieldOff,
  CheckCircle2, AlertTriangle, XCircle, Clock, RefreshCw,
  Loader2, ChevronDown, ChevronUp, ArrowRight, Info,
  TrendingDown, Zap, Lock, Activity, Link2, GitCommit
} from "lucide-react";
import { toast } from "sonner";

// ── Style maps ─────────────────────────────────────────────────────────────

const GATE_ICONS = {
  DESIGN:     Shield,
  COMMERCIAL: Activity,
  PARTNER:    Link2,
  OPERATIONS: Zap,
  EVENT_DAY:  CheckCircle2,
};

const GATE_LABELS = {
  DESIGN:     "Design",
  COMMERCIAL: "Commercial",
  PARTNER:    "Partner",
  OPERATIONS: "Operations",
  EVENT_DAY:  "Event Day",
};

const STATE_STYLE = {
  READY:          { bg: "bg-[#EAF5EE]", text: "text-[#2E844A]", border: "border-[#A3D3AE]", dot: "bg-[#2E844A]" },
  IN_PROGRESS:    { bg: "bg-[#FFF4E5]", text: "text-[#8C4B00]", border: "border-[#F5C45E]", dot: "bg-[#DD7A00]" },
  BLOCKED:        { bg: "bg-[#FCEBEC]", text: "text-[#BA0517]", border: "border-[#F59DAB]", dot: "bg-[#BA0517]" },
  NOT_STARTED:    { bg: "bg-[#F4F6F8]", text: "text-[#706E6B]", border: "border-[#E5E5E5]", dot: "bg-[#706E6B]" },
  NOT_APPLICABLE: { bg: "bg-[#F8F9FA]", text: "text-[#9B9B9B]", border: "border-[#D8D8D8]", dot: "bg-[#9B9B9B]" },
  // Legacy aliases (backward compat during transition)
  ATTENTION:  { bg: "bg-[#FFF4E5]", text: "text-[#8C4B00]", border: "border-[#F5C45E]", dot: "bg-[#DD7A00]" },
  NOT_READY:  { bg: "bg-[#F4F6F8]", text: "text-[#706E6B]", border: "border-[#E5E5E5]", dot: "bg-[#706E6B]" },
};

const RISK_STYLE = {
  AT_RISK:         { bg: "bg-[#FCEBEC]",  text: "text-[#BA0517]",  border: "border-l-[#BA0517]",  label: "At Risk"          },
  BLOCKED:         { bg: "bg-[#FFF1E5]",  text: "text-[#8C4B00]",  border: "border-l-[#DD7A00]",  label: "Blocked"          },
  WAITING_ON:      { bg: "bg-[#FFF8E1]",  text: "text-[#8C4B00]",  border: "border-l-[#F5C45E]",  label: "Waiting On"       },
  DOWNSTREAM_RISK: { bg: "bg-[#F8F9FA]",  text: "text-[#444444]",  border: "border-l-[#B0B0B0]",  label: "Downstream Risk"  },
};

function StateBadge({ state, compact = false }) {
  const s = STATE_STYLE[state] || STATE_STYLE.NOT_STARTED;
  const icons = {
    READY: CheckCircle2, IN_PROGRESS: AlertTriangle, BLOCKED: XCircle,
    NOT_STARTED: Clock, NOT_APPLICABLE: ShieldOff,
    // legacy
    ATTENTION: AlertTriangle, NOT_READY: Clock,
  };
  const Icon = icons[state] || Clock;
  const label = {
    NOT_STARTED: "Not Started", IN_PROGRESS: "In Progress",
    READY: "Ready", BLOCKED: "Blocked", NOT_APPLICABLE: "N/A",
    // legacy
    ATTENTION: "In Progress", NOT_READY: "Not Started",
  }[state] || state;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold border ${s.bg} ${s.text} ${s.border}`}>
      <Icon className="w-3 h-3" />
      {compact ? label.split(" ")[0] : label}
    </span>
  );
}

function ProgressBar({ pct, state }) {
  const colors = {
    READY: "bg-[#2E844A]", IN_PROGRESS: "bg-[#DD7A00]", BLOCKED: "bg-[#BA0517]",
    NOT_STARTED: "bg-[#B0B0B0]", NOT_APPLICABLE: "bg-[#D8D8D8]",
    // legacy
    ATTENTION: "bg-[#DD7A00]", NOT_READY: "bg-[#B0B0B0]",
  };
  return (
    <div className="w-full bg-[#F4F6F8] rounded-full h-2 overflow-hidden">
      <div
        className={`h-2 rounded-full transition-all duration-700 ${colors[state] || colors.NOT_STARTED}`}
        style={{ width: `${pct}%` }}
      />
    </div>
  );
}

// ── Gate Card ─────────────────────────────────────────────────────────────

function GateCard({ gate, expanded, onToggle }) {
  const s = STATE_STYLE[gate.state] || STATE_STYLE.NOT_STARTED;
  const Icon = GATE_ICONS[gate.gate] || Shield;
  const label = GATE_LABELS[gate.gate] || gate.gate;
  const hasBlockers = gate.blockers?.length > 0;
  const hasWarnings = gate.warnings?.length > 0;
  const isNA = gate.state === "NOT_APPLICABLE";

  return (
    <div
      className={`rounded-xl border ${s.border} ${s.bg} transition-all duration-200 ${isNA ? "opacity-60" : ""}`}
      data-testid={`gate-card-${gate.gate?.toLowerCase()}`}
    >
      {/* Card Header */}
      <div className="p-4">
        <div className="flex items-start justify-between gap-2 mb-3">
          <div className="flex items-center gap-2">
            <div className={`p-1.5 rounded-lg ${s.bg}`}>
              <Icon className={`w-4 h-4 ${s.text}`} />
            </div>
            <div>
              <p className="text-xs font-semibold text-[#444444] uppercase tracking-wide">{label}</p>
              <p className={`text-xs font-bold mt-0.5 ${s.text}`}>{isNA ? "—" : `${gate.completion_pct ?? 0}%`}</p>
            </div>
          </div>
          <StateBadge state={gate.state} compact />
        </div>

        {/* Responsible owner */}
        {gate.responsible_owner && !isNA && (
          <p className="text-[10px] text-[#706E6B] mb-2 flex items-center gap-1">
            <Lock className="w-2.5 h-2.5" />
            Owner: <span className="font-medium text-[#444444]">{gate.responsible_owner.replace(/_/g, " ")}</span>
          </p>
        )}

        {/* Progress */}
        {!isNA && <ProgressBar pct={gate.completion_pct ?? 0} state={gate.state} />}
        <p className="text-[10px] text-[#706E6B] mt-1.5">
          {isNA ? "Not applicable for this event type" : `${gate.requirements_satisfied}/${gate.requirements_total} requirements`}
        </p>

        {/* Top blocker / warning */}
        {hasBlockers && !isNA && (
          <div className="mt-2 text-xs text-[#BA0517] bg-[#FCEBEC] rounded-lg px-2.5 py-1.5 leading-snug">
            <XCircle className="inline w-3 h-3 mr-1" />
            {gate.blockers[0]?.message?.slice(0, 80) || "Requirement not satisfied"}
            {gate.blockers.length > 1 && <span className="text-[10px] ml-1 opacity-70">+{gate.blockers.length - 1} more</span>}
          </div>
        )}
        {!hasBlockers && hasWarnings && !isNA && (
          <div className="mt-2 text-xs text-[#8C4B00] bg-[#FFF4E5] rounded-lg px-2.5 py-1.5 leading-snug">
            <AlertTriangle className="inline w-3 h-3 mr-1" />
            {gate.warnings[0]?.message?.slice(0, 80)}
            {gate.warnings.length > 1 && <span className="text-[10px] ml-1 opacity-70">+{gate.warnings.length - 1} more</span>}
          </div>
        )}

        {/* Expand toggle */}
        {!isNA && (hasBlockers || hasWarnings || gate.requirement_results?.length) ? (
          <button
            onClick={onToggle}
            className={`mt-2.5 flex items-center gap-1 text-[10px] font-medium ${s.text} hover:underline`}
          >
            {expanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
            {expanded ? "Hide" : "Show"} details
          </button>
        ) : null}
      </div>

      {/* Expanded requirement results */}
      {expanded && !isNA && gate.requirement_results && (
        <div className="border-t border-[#E5E5E5] bg-white/60 rounded-b-xl px-4 py-3 space-y-2">
          {gate.requirement_results.map((r) => (
            <div key={r.req_id} className="flex items-start gap-2 text-xs">
              {r.satisfied
                ? <CheckCircle2 className="w-3.5 h-3.5 text-[#2E844A] flex-shrink-0 mt-0.5" />
                : r.blockers?.length
                  ? <XCircle className="w-3.5 h-3.5 text-[#BA0517] flex-shrink-0 mt-0.5" />
                  : <AlertTriangle className="w-3.5 h-3.5 text-[#DD7A00] flex-shrink-0 mt-0.5" />
              }
              <div className="min-w-0 flex-1">
                <span className="font-mono text-[10px] text-[#706E6B] mr-1.5">{r.req_id}</span>
                <span className="text-[#444444]">{r.description}</span>
                {r.evidence && (
                  <p className="text-[#2E844A] mt-0.5 text-[10px] flex items-center gap-0.5">
                    <CheckCircle2 className="w-2.5 h-2.5" />
                    {r.evidence}
                  </p>
                )}
                {r.blockers?.[0] && (
                  <p className="text-[#BA0517] mt-0.5 text-[10px]">{r.blockers[0].message?.slice(0, 120)}</p>
                )}
                {r.warnings?.[0] && !r.blockers?.length && (
                  <p className="text-[#8C4B00] mt-0.5 text-[10px]">{r.warnings[0].message?.slice(0, 120)}</p>
                )}
              </div>
              <span className="text-[10px] text-[#706E6B] font-mono whitespace-nowrap">
                {r.satisfied_count ?? 0}/{r.count ?? 1}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Critical Path ─────────────────────────────────────────────────────────

function CriticalPathSection({ criticalPath }) {
  const { chain = [], at_risk = [], directly_blocked = [], waiting_on = [], downstream_risk = [] } = criticalPath || {};
  if (chain.length === 0) return null;

  const total = at_risk.length + directly_blocked.length + waiting_on.length + downstream_risk.length;

  return (
    <div className="bg-white border border-[#E5E5E5] rounded-xl overflow-hidden" data-testid="critical-path-section">
      <div className="flex items-center justify-between px-5 py-3 border-b border-[#F4F6F8]">
        <div>
          <h4 className="text-sm font-semibold text-[#181818]">Dependency-Aware Critical Path</h4>
          <p className="text-xs text-[#706E6B] mt-0.5">{total} milestones at risk — resolve root causes first</p>
        </div>
        <div className="flex gap-2 text-[10px]">
          {[
            { color: "bg-[#BA0517]", label: `${at_risk.length} At Risk` },
            { color: "bg-[#DD7A00]", label: `${directly_blocked.length} Blocked` },
            { color: "bg-[#F5C45E]", label: `${waiting_on.length + downstream_risk.length} Downstream` },
          ].filter(x => parseInt(x.label) > 0).map(x => (
            <span key={x.label} className="flex items-center gap-1 text-[#706E6B]">
              <span className={`w-2 h-2 rounded-full ${x.color}`} />
              {x.label}
            </span>
          ))}
        </div>
      </div>

      <div className="divide-y divide-[#F4F6F8]">
        {chain.map((node, i) => {
          const r = RISK_STYLE[node.risk_level] || RISK_STYLE.DOWNSTREAM_RISK;
          const isLast = i === chain.length - 1;
          return (
            <div key={node.key} className={`flex items-start gap-3 px-5 py-3 border-l-4 ${r.border} ${r.bg}`}
              data-testid={`critical-path-node-${node.key.toLowerCase()}`}>
              <div className="flex flex-col items-center pt-1">
                <div className={`w-2 h-2 rounded-full ${r.text.replace('text', 'bg')} flex-shrink-0`} />
                {!isLast && <div className="w-px h-full min-h-[12px] bg-[#E5E5E5] mt-1" />}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-xs font-semibold text-[#181818]">{node.title}</span>
                  <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded ${r.bg} ${r.text} border ${r.border.replace('border-l', 'border')}`}>
                    {r.label}
                  </span>
                  {node.gate && (
                    <span className="text-[10px] text-[#706E6B] font-mono">{node.gate}</span>
                  )}
                  {node.is_overdue && (
                    <span className="text-[10px] text-[#BA0517] font-medium">OVERDUE</span>
                  )}
                </div>
                {node.blocked_by?.length > 0 && (
                  <p className="text-[10px] text-[#706E6B] mt-0.5">
                    Blocked by: {node.blocked_by.join(", ")}
                  </p>
                )}
                {node.due_at && (
                  <p className="text-[10px] text-[#B0B0B0] mt-0.5">
                    Due {new Date(node.due_at).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}
                  </p>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Signal Summary ────────────────────────────────────────────────────────

function SignalSummary({ signals }) {
  if (!signals) return null;
  const items = [
    { label: "Critical Issues",     value: signals.critical_issues,          color: signals.critical_issues > 0 ? "text-[#BA0517]" : "text-[#2E844A]",   bg: signals.critical_issues > 0 ? "bg-[#FCEBEC]" : "bg-[#EAF5EE]" },
    { label: "Open Issues",         value: signals.open_issues,              color: signals.open_issues > 0 ? "text-[#8C4B00]" : "text-[#2E844A]",       bg: signals.open_issues > 0 ? "bg-[#FFF4E5]" : "bg-[#EAF5EE]"   },
    { label: "Pending Approvals",   value: signals.pending_approvals,        color: signals.pending_approvals > 0 ? "text-[#8C4B00]" : "text-[#2E844A]", bg: signals.pending_approvals > 0 ? "bg-[#FFF4E5]" : "bg-[#EAF5EE]" },
    { label: "Open Changes",        value: signals.open_changes,             color: signals.high_critical_changes > 0 ? "text-[#8C4B00]" : "text-[#444444]", bg: signals.high_critical_changes > 0 ? "bg-[#FFF4E5]" : "bg-white" },
    { label: "Overdue Milestones",  value: signals.overdue_milestones,       color: signals.overdue_milestones > 0 ? "text-[#BA0517]" : "text-[#2E844A]", bg: signals.overdue_milestones > 0 ? "bg-[#FCEBEC]" : "bg-[#EAF5EE]" },
    { label: "Milestones Done",     value: `${signals.completed_milestones}/${signals.total_milestones}`, color: "text-[#444444]", bg: "bg-white" },
  ];
  return (
    <div data-testid="signal-summary">
      <h4 className="text-sm font-semibold text-[#181818] mb-3">Signal Summary</h4>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        {items.map(item => (
          <div key={item.label} className={`${item.bg} border border-[#E5E5E5] rounded-xl p-3`}>
            <p className="text-[10px] text-[#706E6B] uppercase tracking-wide mb-1">{item.label}</p>
            <p className={`text-xl font-bold ${item.color}`}>{item.value}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Milestone List ────────────────────────────────────────────────────────

function MilestoneSection({ eventId }) {
  const [milestones, setMilestones] = useState(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(null);
  const [gateFilter, setGateFilter] = useState("ALL");

  const load = useCallback(() => {
    api.get(`/events/${eventId}/milestones`)
      .then(r => setMilestones(r.data.milestones || []))
      .catch(() => setMilestones([]))
      .finally(() => setLoading(false));
  }, [eventId]);

  useEffect(() => { load(); }, [load]);

  async function handleComplete(ms) {
    if (ms.status === "COMPLETED") return;
    setUpdating(ms.id);
    try {
      await api.patch(`/events/${eventId}/milestones/${ms.id}`, { status: "COMPLETED" });
      toast.success(`${ms.title} marked Complete`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Update failed");
    } finally {
      setUpdating(null);
    }
  }

  if (loading) return <div className="flex items-center justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-[#0176D3]" /></div>;
  if (!milestones?.length) return (
    <div className="text-center py-8">
      <p className="text-sm text-[#706E6B] mb-3">No milestones found for this event.</p>
      <button
        data-testid="generate-milestones-btn"
        onClick={async () => {
          try {
            const r = await api.post(`/events/${eventId}/milestones/generate`);
            toast.success(r.data.message || "Milestones generated");
            load();
          } catch (e) { toast.error(e.response?.data?.detail || "Generation failed"); }
        }}
        className="px-4 py-2 bg-[#0176D3] text-white rounded-lg text-sm hover:bg-[#0161B0] transition-colors"
      >
        Generate Milestones
      </button>
    </div>
  );

  const gates = ["ALL", "DESIGN", "COMMERCIAL", "PARTNER", "OPERATIONS", "EVENT_DAY"];
  const filtered = gateFilter === "ALL" ? milestones : milestones.filter(m => m.gate === gateFilter);

  const STATUS_STYLE = {
    COMPLETED:   { dot: "bg-[#2E844A]", text: "text-[#2E844A]" },
    IN_PROGRESS: { dot: "bg-[#0176D3]", text: "text-[#0176D3]" },
    PENDING:     { dot: "bg-[#B0B0B0]", text: "text-[#706E6B]" },
    CANCELLED:   { dot: "bg-[#E5E5E5]", text: "text-[#B0B0B0]" },
    BLOCKED:     { dot: "bg-[#BA0517]", text: "text-[#BA0517]" },
  };

  return (
    <div data-testid="milestone-list">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <h4 className="text-sm font-semibold text-[#181818]">Milestones</h4>
        <div className="flex gap-1 flex-wrap">
          {gates.map(g => (
            <button key={g} onClick={() => setGateFilter(g)}
              className={`px-2.5 py-1 rounded-full text-[10px] font-medium border transition-colors ${
                gateFilter === g ? "bg-[#0176D3] text-white border-[#0176D3]" : "bg-white text-[#706E6B] border-[#E5E5E5] hover:border-[#0176D3]"
              }`}>
              {g === "ALL" ? "All" : GATE_LABELS[g] || g}
            </button>
          ))}
        </div>
      </div>

      <div className="space-y-2">
        {filtered.map(ms => {
          const ss = STATUS_STYLE[ms.status] || STATUS_STYLE.PENDING;
          return (
            <div key={ms.id}
              className={`flex items-start gap-3 p-3 rounded-xl border ${ms.is_overdue && ms.status !== "COMPLETED" ? "border-[#F59DAB] bg-[#FCEBEC]/30" : "border-[#E5E5E5] bg-white"}`}
              data-testid={`milestone-row-${ms.template_item_key}`}>
              <div className={`w-2 h-2 rounded-full flex-shrink-0 mt-1.5 ${ss.dot} ${ms.is_overdue && ms.status !== "COMPLETED" ? "animate-pulse" : ""}`} />
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <p className="text-xs font-semibold text-[#181818]">{ms.title}</p>
                    <p className={`text-[10px] ${ss.text} mt-0.5`}>
                      {ms.status}
                      {ms.is_critical && <span className="ml-1.5 text-[#BA0517] font-medium">CRITICAL</span>}
                      {ms.is_overdue && ms.status !== "COMPLETED" && <span className="ml-1.5 text-[#BA0517] font-medium">OVERDUE</span>}
                    </p>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <span className="text-[10px] text-[#706E6B] font-mono">{ms.gate}</span>
                    {ms.status !== "COMPLETED" && ms.status !== "CANCELLED" && (
                      <button
                        onClick={() => handleComplete(ms)}
                        disabled={updating === ms.id}
                        data-testid={`milestone-complete-btn-${ms.template_item_key}`}
                        className="px-2 py-0.5 bg-[#EAF5EE] text-[#2E844A] border border-[#A3D3AE] rounded text-[10px] font-medium hover:bg-[#2E844A] hover:text-white transition-colors disabled:opacity-50"
                      >
                        {updating === ms.id ? <Loader2 className="w-3 h-3 animate-spin" /> : "Done"}
                      </button>
                    )}
                  </div>
                </div>
                {ms.due_at && (
                  <p className="text-[10px] text-[#B0B0B0] mt-0.5">
                    Due {new Date(ms.due_at).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}
                    {ms.depends_on?.length > 0 && <span className="ml-2 text-[#706E6B]">→ needs: {ms.depends_on.join(", ")}</span>}
                  </p>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Main EventReadiness component ─────────────────────────────────────────

export default function EventReadiness({ eventId }) {
  const [readiness, setReadiness] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [expandedGate, setExpandedGate] = useState(null);
  const [activeSection, setActiveSection] = useState("gates"); // gates | path | milestones

  const load = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    else setRefreshing(true);
    try {
      const r = await api.get(`/events/${eventId}/readiness`);
      setReadiness(r.data);
    } catch {
      toast.error("Failed to load readiness data");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [eventId]);

  useEffect(() => { load(); }, [load]);

  if (loading) return (
    <div className="flex items-center justify-center py-20" data-testid="readiness-loading">
      <Loader2 className="w-8 h-8 animate-spin text-[#0176D3]" />
    </div>
  );

  if (!readiness) return (
    <div className="text-center py-12" data-testid="readiness-error">
      <p className="text-sm text-[#706E6B]">Readiness data unavailable. No active policy may be configured.</p>
    </div>
  );

  const s = STATE_STYLE[readiness.overall_state] || STATE_STYLE.NOT_READY;
  const hasPath = readiness.critical_path?.has_blockers;

  return (
    <div className="space-y-5" data-testid="event-readiness-panel">
      {/* ── Overall readiness header ── */}
      <div className={`${s.bg} border ${s.border} rounded-2xl p-5`}>
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-3">
            <div className={`p-2.5 rounded-xl border ${s.border} bg-white/70`}>
              {readiness.overall_state === "READY"
                ? <ShieldCheck className={`w-6 h-6 ${s.text}`} />
                : readiness.overall_state === "BLOCKED"
                  ? <ShieldX className={`w-6 h-6 ${s.text}`} />
                  : readiness.overall_state === "ATTENTION"
                    ? <ShieldAlert className={`w-6 h-6 ${s.text}`} />
                    : <ShieldOff className={`w-6 h-6 ${s.text}`} />
              }
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h3 className="text-base font-bold text-[#181818]">Event Readiness</h3>
                <StateBadge state={readiness.overall_state} />
                {refreshing && <Loader2 className="w-3.5 h-3.5 animate-spin text-[#706E6B]" />}
              </div>
              <p className="text-xs text-[#706E6B] mt-0.5">
                {readiness.requirements_satisfied}/{readiness.requirements_total} requirements across 5 gates ·{" "}
                Evaluated {new Date(readiness.evaluated_at).toLocaleString("en-IN", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}
                {readiness.evaluation_clock_source === "UAT_OVERRIDE" && (
                  <span className="ml-1.5 text-[10px] text-[#8C4B00] font-medium bg-[#FFF4E5] px-1.5 py-0.5 rounded">UAT CLOCK</span>
                )}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <div className="text-right">
              <p className={`text-4xl font-black ${s.text}`}>{readiness.overall_pct}%</p>
              <p className="text-[10px] text-[#706E6B] mt-0.5">Overall</p>
            </div>
            <button
              data-testid="readiness-refresh-btn"
              onClick={() => load(true)}
              disabled={refreshing}
              className="p-2 rounded-lg border border-[#E5E5E5] bg-white hover:bg-[#FAFAFA] text-[#706E6B] transition-colors"
            >
              <RefreshCw className={`w-4 h-4 ${refreshing ? "animate-spin" : ""}`} />
            </button>
          </div>
        </div>
        <div className="mt-4">
          <ProgressBar pct={readiness.overall_pct} state={readiness.overall_state} />
        </div>
      </div>

      {/* ── Section nav ── */}
      <div className="flex gap-1 border-b border-[#E5E5E5]">
        {[
          { id: "gates",      label: "Gate Evaluation" },
          { id: "path",       label: `Critical Path${hasPath ? " ⚠" : ""}` },
          { id: "milestones", label: "Milestones" },
        ].map(sec => (
          <button key={sec.id} onClick={() => setActiveSection(sec.id)}
            data-testid={`readiness-section-${sec.id}`}
            className={`px-4 py-2 text-xs font-medium border-b-2 transition-colors ${
              activeSection === sec.id
                ? "border-[#0176D3] text-[#0176D3]"
                : "border-transparent text-[#706E6B] hover:text-[#444444]"
            }`}>
            {sec.label}
          </button>
        ))}
      </div>

      {/* ── Gates grid ── */}
      {activeSection === "gates" && (
        <div className="space-y-5">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {readiness.gates?.map(gate => (
              <GateCard
                key={gate.gate}
                gate={gate}
                expanded={expandedGate === gate.gate}
                onToggle={() => setExpandedGate(expandedGate === gate.gate ? null : gate.gate)}
              />
            ))}
          </div>
          <SignalSummary signals={readiness.signal_summary} />
        </div>
      )}

      {/* ── Critical path ── */}
      {activeSection === "path" && (
        <div className="space-y-4">
          {hasPath
            ? <CriticalPathSection criticalPath={readiness.critical_path} />
            : (
              <div className="text-center py-12 bg-[#EAF5EE] border border-[#A3D3AE] rounded-xl">
                <CheckCircle2 className="w-10 h-10 text-[#2E844A] mx-auto mb-2" />
                <p className="text-sm font-semibold text-[#2E844A]">No dependency blockers</p>
                <p className="text-xs text-[#706E6B] mt-1">All milestone dependencies are satisfied.</p>
              </div>
            )
          }
        </div>
      )}

      {/* ── Milestones ── */}
      {activeSection === "milestones" && (
        <MilestoneSection eventId={eventId} />
      )}

      {/* Policy attribution */}
      {readiness.gates?.[0]?.policy_id && (
        <p className="text-[10px] text-[#B0B0B0] text-center">
          Evaluated against active readiness policies · Policy IDs:{" "}
          {[...new Set(readiness.gates.map(g => g.policy_version ? `v${g.policy_version}` : null).filter(Boolean))].join(", ")}
        </p>
      )}
    </div>
  );
}
